package diana.soleil.design.iraniancanadienbazzar.feature.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.PersonAvatar
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.core.model.BlockedUser
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_blocked_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_blocked_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_blocked_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_blocked_unblock
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_blocked_users
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.profile_role_community
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/** Live blocked list (name snapshots) + unblock, backed by `users/{uid}/blockedUsers`. */
class BlockedUsersViewModel(private val blockRepository: BlockRepository) : ViewModel() {

    val state: StateFlow<List<BlockedUser>> = blockRepository.observeBlockedUsers()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    /** Unblocks [uid]; the live stream drops the row and their content reappears everywhere. */
    fun unblock(uid: String) {
        viewModelScope.launch { blockRepository.unblock(uid) }
    }
}

/** Stateful entry for the blocked-users management screen (Profile → Blocked users). */
@Composable
fun BlockedUsersRoute(
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: BlockedUsersViewModel = koinViewModel(),
) {
    val blocked by viewModel.state.collectAsStateWithLifecycle()
    BlockedUsersScreen(
        blocked = blocked,
        onBack = onBack,
        onUnblock = viewModel::unblock,
        modifier = modifier,
    )
}

/**
 * People the signed-in user has blocked: initial-avatar + name snapshot + Unblock. Blocking is
 * client-side filtering only (the target is never notified), so unblocking instantly restores
 * their posts, comments, listings and threads.
 */
@Composable
private fun BlockedUsersScreen(
    blocked: List<BlockedUser>,
    onBack: () -> Unit,
    onUnblock: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.background(AppTheme.colors.background).appContentWidth(AppTheme.sizes.contentMaxWidth).fillMaxHeight()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.surface)
                .statusBarsPadding().navigationBarsPadding()
                .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xs),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.profile_blocked_back_a11y),
                onClick = onBack,
            )
            Text(
                text = stringResource(Res.string.profile_blocked_users),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onSurface,
                modifier = Modifier.weight(1f),
            )
        }

        if (blocked.isEmpty()) {
            EmptyState(
                title = stringResource(Res.string.profile_blocked_empty_title),
                description = stringResource(Res.string.profile_blocked_empty_desc),
            )
        } else {
            LazyColumn(
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                items(blocked, key = { it.uid }) { user ->
                    BlockedUserRow(user = user, onUnblock = { onUnblock(user.uid) })
                }
            }
        }
    }
}

@Composable
private fun BlockedUserRow(
    user: BlockedUser,
    onUnblock: () -> Unit,
) {
    // Chat blocks can't always snapshot a display name (users/{uid} is unreadable to other
    // clients), so an empty snapshot falls back to the generic community-member label.
    val displayName = user.name.ifBlank { stringResource(Res.string.profile_role_community) }
    AppCard(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
        ) {
            PersonAvatar(
                id = user.uid,
                name = displayName,
                size = AppTheme.sizes.avatarSm,
                textStyle = AppTheme.typography.labelMd,
            )
            Text(
                text = displayName,
                style = AppTheme.typography.labelMd,
                color = AppTheme.colors.onSurface,
                modifier = Modifier.weight(1f),
            )
            AppButton(
                text = stringResource(Res.string.profile_blocked_unblock),
                onClick = onUnblock,
                variant = AppButtonVariant.Secondary,
            )
        }
    }
}
